/**
 * ConnectShopifySheet — "Push to Shopify" entry point from the Inspector.
 *
 * Two states:
 *   - Not connected: empty-state CTA to connect a store (unchanged).
 *   - Connected: a real, searchable product picker. Selecting a product
 *     pushes the current asset into that product's media gallery via
 *     productService.pushImageToShopify (productCreateMedia mutation on the
 *     already-granted write_products scope — no re-auth).
 *
 * Was: a bottom Sheet with broken `sm:max-w-md sm:mx-auto` styling and
 * missing `rounded-t-xl` on mobile. Now: a `<Dialog responsive>` so we get
 * the canonical centered modal on desktop + bottom sheet (with rounded-t-xl
 * + drag handle) on mobile, matching the rest of the design system.
 *
 * Spec lineage:
 *   - DESIGN-COMPONENTS.md §Dialog — `size="sm"` for confirm-style empty
 *     state, `rounded-xl` borders, `bg-popover/95 backdrop-blur-xl`
 *   - DESIGN-PATTERNS.md §Empty States — Empty primitive composed inside
 *     a Dialog wrapper, NOT a bare Sheet
 *   - Quiet List contract — product rows are title + one meta line, no
 *     dividers, whole row clickable (`PanelListRow`).
 *
 * Connect button uses the canonical `useSettingsDialog().openSettings(
 * 'integrations')` overlay (per parallel-session commit 24d932365 that
 * standardized this across the app), NOT a route navigation. The settings
 * dialog opens over the current page and closes back to wherever the user
 * was — no context loss.
 */
import { ArrowSquareOutIcon, PackageIcon, ShoppingCartIcon } from '@phosphor-icons/react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { useSettingsDialog } from '@/components/settings/SettingsDialogProvider';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia } from '@/components/ui/empty';
import { PanelListRow } from '@/components/ui/panel-list-row';
import { SearchBar } from '@/components/ui/search-bar';
import { Spinner } from '@/components/ui/spinner';
import { useShopifyConnection, useShopifyProducts } from '@/hooks/useShopifyProducts';
import { trackProductEvent } from '@/lib/analytics/product-events';
import { productService } from '@/services/data/ProductService';
import { formatPrice } from '@/types/product';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Surface that opened the dialog — used for telemetry segmentation. */
  origin: 'image-chip' | 'image-action-bar' | 'video-action-bar';
  /** Asset URL to push into the selected product's media gallery. */
  assetUrl?: string;
}

export function ConnectShopifySheet({ open, onOpenChange, origin, assetUrl }: Props) {
  const { openSettings } = useSettingsDialog();
  const { hasConnection, isConnectionLoading } = useShopifyConnection();

  const handleConnect = () => {
    trackProductEvent('push_to_shopify_connect_clicked', {
      origin,
      has_asset: !!assetUrl,
    });
    onOpenChange(false);
    openSettings('integrations');
  };

  const handleDismiss = () => {
    trackProductEvent('push_to_shopify_dismissed', { origin });
    onOpenChange(false);
  };

  if (!isConnectionLoading && hasConnection) {
    return (
      <ShopifyProductPickerDialog
        open={open}
        onOpenChange={onOpenChange}
        origin={origin}
        assetUrl={assetUrl}
      />
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm" responsive className="p-6 gap-0">
        <div className="flex flex-col items-center text-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted/60">
            <ShoppingCartIcon className="size-6 text-foreground/70" weight="regular" />
          </div>
          <DialogTitle>Push images to your Shopify product pages</DialogTitle>
          <DialogDescription className="text-detail leading-relaxed text-muted-foreground">
            Connect your Shopify store and Mora can push generated product images straight into your
            product media — no manual upload, no aspect-ratio guesswork.
          </DialogDescription>
        </div>

        <div className="mt-5 flex flex-col gap-2">
          <Button variant="neutral" size="default" onClick={handleConnect}>
            <ArrowSquareOutIcon className="size-4" weight="regular" />
            Connect Shopify
          </Button>
          <Button variant="outline" size="default" onClick={handleDismiss}>
            Not now
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface ProductPickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  origin: Props['origin'];
  assetUrl?: string;
}

function ShopifyProductPickerDialog({ open, onOpenChange, origin, assetUrl }: ProductPickerProps) {
  const [search, setSearch] = useState('');
  const [pushingId, setPushingId] = useState<string | null>(null);
  const { products, isLoading, searchProducts, clearSearch } = useShopifyProducts();

  const visibleProducts = useMemo(() => products, [products]);

  const handleSearchChange = (value: string) => {
    setSearch(value);
    if (value.trim()) {
      searchProducts(value);
    } else {
      clearSearch();
    }
  };

  const handleSelectProduct = async (productId: string, title: string) => {
    if (!assetUrl || pushingId) return;
    setPushingId(productId);
    trackProductEvent('push_to_shopify_product_selected', { origin, has_asset: !!assetUrl });

    const result = await productService.pushImageToShopify(productId, assetUrl, title);
    setPushingId(null);

    if (result.error) {
      toast.error(`Couldn't push to Shopify: ${result.error.message}`);
      return;
    }

    toast.success(`Added to ${title}.`);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm" responsive className="p-6 gap-0">
        <DialogTitle>Push to a Shopify product</DialogTitle>
        <DialogDescription className="text-detail text-muted-foreground">
          Pick a product and this image is added straight to its media gallery.
        </DialogDescription>

        <SearchBar
          variant="neutral"
          size="sm"
          value={search}
          onChange={handleSearchChange}
          placeholder="Search products…"
          className="mt-4"
        />

        <div className="mt-3 max-h-[360px] overflow-y-auto -mx-2">
          {isLoading ? (
            <div className="flex items-center justify-center gap-2 py-8 text-caption text-muted-foreground">
              <Spinner size="sm" />
              Loading products…
            </div>
          ) : visibleProducts.length === 0 ? (
            <Empty className="border-0 py-2">
              <EmptyHeader className="gap-2">
                <EmptyMedia variant="icon">
                  <PackageIcon />
                </EmptyMedia>
                <EmptyDescription className="text-caption text-muted-foreground/80 leading-relaxed max-w-[240px]">
                  {search ? 'No products match that search.' : 'No products found in your store.'}
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          ) : (
            visibleProducts.map((product) => {
              const isPushing = pushingId === product.id;
              const price = formatPrice(product.min_price);
              return (
                <PanelListRow
                  key={product.id}
                  onClick={() =>
                    void handleSelectProduct(product.shopify_product_id, product.title)
                  }
                  title={product.title}
                  meta={isPushing ? 'Pushing…' : price}
                  leading={
                    product.images?.[0]?.url ? (
                      <img
                        src={product.images[0].url}
                        alt=""
                        className="size-full rounded-md object-cover"
                      />
                    ) : (
                      <PackageIcon className="size-3.5 text-muted-foreground/50" weight="regular" />
                    )
                  }
                  trailing={isPushing ? <Spinner size="sm" /> : undefined}
                  className={pushingId && !isPushing ? 'pointer-events-none opacity-50' : undefined}
                />
              );
            })
          )}
        </div>

        <div className="mt-4 flex justify-end border-t border-border/40 pt-3">
          <Button variant="flat" size="sm" className="h-8 px-4" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
